import { createStitches } from '@stitches/react'
import type * as Stitches from '@stitches/react'

import BaseTokens from '@grupoboticario/flora-tokens/gbInstitucional/web/ds'

import {
  beautybox,
  belezanaweb,
  blzPro,
  boticario,
  eudora,
  gbInstitucional,
  oui,
  quemDisseBerenice,
  toqueto,
  vult,
  altoContraste,
  monocromatico,
  revendedores,
  casaMagalhaesOutono,
  casaMagalhaesNoite,
  casaMagalhaesCereja,
  casaMagalhaesAmazonia,
  casaMagalhaesOceano,
  associacaoGrupoBoticario,
  drJones,
  truss,
  fidelidadeOBoticario,
  fidelidadeQuemDisseBerenice,
  mooz,
} from './themes'

export type FloraVariants<T> = Stitches.VariantProps<T>

export const Tokens = BaseTokens

const { media, ...baseTheme } = Tokens

type PropertyValue<T extends keyof Stitches.CSSProperties> =
  | Stitches.PropertyValue<T>
  | Stitches.ScaleValue<T>
  | string
  | number

export const flora = createStitches({
  prefix: 'flora-',
  theme: baseTheme,
  media,
  utils: {
    p: (value: PropertyValue<'padding'>) => ({
      padding: value,
    }),
    pt: (value: PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
    }),
    pr: (value: PropertyValue<'paddingRight'>) => ({
      paddingRight: value,
    }),
    pb: (value: PropertyValue<'paddingBottom'>) => ({
      paddingBottom: value,
    }),
    pl: (value: PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
    }),
    px: (value: PropertyValue<'padding'>) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: PropertyValue<'padding'>) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
    m: (value: PropertyValue<'margin'>) => ({
      margin: value,
    }),
    mt: (value: PropertyValue<'marginTop'>) => ({
      marginTop: value,
    }),
    mr: (value: PropertyValue<'marginRight'>) => ({
      marginRight: value,
    }),
    mb: (value: PropertyValue<'marginBottom'>) => ({
      marginBottom: value,
    }),
    ml: (value: PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
    }),
    mx: (value: PropertyValue<'margin'>) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: PropertyValue<'margin'>) => ({
      marginTop: value,
      marginBottom: value,
    }),

    ta: (value: PropertyValue<'textAlign'>) => ({ textAlign: value }),

    fd: (value: PropertyValue<'flexDirection'>) => ({ flexDirection: value }),
    fw: (value: PropertyValue<'flexWrap'>) => ({ flexWrap: value }),

    ai: (value: PropertyValue<'alignItems'>) => ({ alignItems: value }),
    ac: (value: PropertyValue<'alignContent'>) => ({ alignContent: value }),
    jc: (value: PropertyValue<'justifyContent'>) => ({ justifyContent: value }),
    as: (value: PropertyValue<'alignSelf'>) => ({ alignSelf: value }),
    fg: (value: PropertyValue<'flexGrow'>) => ({ flexGrow: value }),
    fs: (value: PropertyValue<'flexShrink'>) => ({ flexShrink: value }),
    fb: (value: PropertyValue<'flexBasis'>) => ({ flexBasis: value }),

    bc: (value: PropertyValue<'backgroundColor'>) => ({
      backgroundColor: value,
    }),

    br: (value: PropertyValue<'borderRadius'>) => ({
      borderRadius: value,
    }),
    btrr: (value: PropertyValue<'borderTopRightRadius'>) => ({
      borderTopRightRadius: value,
    }),
    bbrr: (value: PropertyValue<'borderBottomRightRadius'>) => ({
      borderBottomRightRadius: value,
    }),
    bblr: (value: PropertyValue<'borderBottomLeftRadius'>) => ({
      borderBottomLeftRadius: value,
    }),
    btlr: (value: PropertyValue<'borderTopLeftRadius'>) => ({
      borderTopLeftRadius: value,
    }),

    bs: (value: PropertyValue<'boxShadow'>) => ({ boxShadow: value }),

    lh: (value: PropertyValue<'lineHeight'>) => ({ lineHeight: value }),

    ox: (value: PropertyValue<'overflowX'>) => ({ overflowX: value }),
    oy: (value: PropertyValue<'overflowY'>) => ({ overflowY: value }),

    pe: (value: PropertyValue<'pointerEvents'>) => ({ pointerEvents: value }),
    us: (value: PropertyValue<'userSelect'>) => ({
      WebkitUserSelect: value,
      userSelect: value,
    }),

    size: (value: PropertyValue<'width'>) => ({
      width: value,
      height: value,
    }),

    linearGradient: (value: PropertyValue<'backgroundImage'>) => ({
      backgroundImage: `linear-gradient(${value})`,
    }),

    appearance: (value: PropertyValue<'appearance'>) => ({
      WebkitAppearance: value,
      appearance: value,
    }),
    userSelect: (value: PropertyValue<'userSelect'>) => ({
      WebkitUserSelect: value,
      userSelect: value,
    }),
    backgroundClip: (value: PropertyValue<'backgroundClip'>) => ({
      WebkitBackgroundClip: value,
      backgroundClip: value,
    }),
    fullscreen: (value: PropertyValue<'position'>) => ({
      position: value,
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    }),
  },
})

export const { config, css, getCssText, globalCss, keyframes, styled, theme } = flora

/**
 * @function
 * @name createTheme
 * @description Use for create a new theme from flora gbInstitucional
 * @tutorial https://flora.gb.tech/docs/overview/styling
 */
export const createTheme = flora.createTheme

export type CSS = Stitches.CSS<typeof config>
export type ComponentCSSProp = { css?: CSS; as?: React.ElementType }
export type SpaceThemeProps = `$${keyof typeof theme['space']}`
export type SizeThemeProps = `$${keyof typeof theme['sizes']}`
export type ColorThemeProps = `$${keyof typeof theme['colors']}`
export type FontSizeThemeProps = `$${keyof typeof theme['fontSizes']}`
export type FontsThemeProps = `$${keyof typeof theme['fonts']}`
export type ShadowsThemeProps = `$${keyof typeof theme['shadows']}`
export type Theme = ReturnType<typeof createTheme>

export const beautyboxTheme = createTheme('beautybox-theme', beautybox)
export const belezanawebTheme = createTheme('belezanaweb-theme', belezanaweb)
export const blzProTheme = createTheme('blz-pro-theme', blzPro)
export const boticarioTheme = createTheme('o-boticario-theme', boticario)
export const eudoraTheme = createTheme('eudora-theme', eudora)
export const gbInstitucionalTheme = createTheme('gb-institucional-theme', gbInstitucional)
export const toQueToTheme = createTheme('toqueto-theme', toqueto)
export const ouiTheme = createTheme('oui-theme', oui)
export const quemDisseBereniceTheme = createTheme('quem-disse-berenice-theme', quemDisseBerenice)
export const vultTheme = createTheme('vult-theme', vult)
export const altoContrasteTheme = createTheme('alto-contraste-theme', altoContraste)
export const monocromaticoTheme = createTheme('monocromatico-theme', monocromatico)
export const revendedoresTheme = createTheme('revendedores-theme', revendedores)
export const casaMagalhaesOutonoTheme = createTheme(
  'casa-magalhaes-outono-theme',
  casaMagalhaesOutono,
)
export const casaMagalhaesNoiteTheme = createTheme('casa-magalhaes-noite-theme', casaMagalhaesNoite)
export const casaMagalhaesCerejaTheme = createTheme(
  'casa-magalhaes-cereja-theme',
  casaMagalhaesCereja,
)
export const casaMagalhaesAmazoniaTheme = createTheme(
  'casa-magalhaes-amazonia-theme',
  casaMagalhaesAmazonia,
)
export const casaMagalhaesOceanoTheme = createTheme(
  'casa-magalhaes-oceano-theme',
  casaMagalhaesOceano,
)
export const associacaoGrupoBoticarioTheme = createTheme(
  'associacao-grupo-boticario-theme',
  associacaoGrupoBoticario,
)
export const drJonesTheme = createTheme('dr-jones-theme', drJones)
export const trussTheme = createTheme('truss-theme', truss)
export const fidelidadeOBoticarioTheme = createTheme(
  'fidelidade-o-boticario-theme',
  fidelidadeOBoticario,
)
export const fidelidadeQuemDisseBereniceTheme = createTheme(
  'fidelidade-quem-disse-berenice-theme',
  fidelidadeQuemDisseBerenice,
)
export const moozTheme = createTheme('mooz-theme', mooz)
